import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Button, Form, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';

import { orderApi, warehouseApi } from 'api';

const OrderTransferModal = ({
  show,
  onHide,
  orderId,
  currentWarehouseId,
  onSuccess,
}) => {
  const [warehouses, setWarehouses] = useState([]);
  const [selectedWarehouseId, setSelectedWarehouseId] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchWarehouses = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await warehouseApi.list();
      const warehouseList = response?.results || response || [];
      // 현재 warehouse 제외
      const filteredWarehouses = warehouseList.filter(
        wh => wh.id !== currentWarehouseId,
      );
      setWarehouses(filteredWarehouses);
    } catch (error) {
      toast.error('Failed to load warehouses');
    } finally {
      setIsLoading(false);
    }
  }, [currentWarehouseId]);

  useEffect(() => {
    if (show) {
      fetchWarehouses();
      setSelectedWarehouseId('');
    }
  }, [show, currentWarehouseId, fetchWarehouses]);

  const handleSubmit = async () => {
    if (!selectedWarehouseId) {
      toast.warning('Please select a warehouse');
      return;
    }

    setIsSubmitting(true);
    try {
      await orderApi.transferOrder(orderId, Number(selectedWarehouseId));
      toast.success('Order transferred successfully');
      onSuccess?.();
      handleClose();
    } catch (error) {
      const status = error?.response?.status;
      const data = error?.response?.data;
      const serverMessage =
        (Array.isArray(data) ? data.join(', ') : null) ||
        data?.detail ||
        data?.message ||
        data?.error ||
        (typeof data === 'string' ? data : null);

      if (status === 403) {
        toast.error(
          `Permission denied: ${
            serverMessage || 'You do not have permission to transfer this order'
          }`,
        );
      } else if (status >= 400 && status < 500) {
        toast.error(serverMessage || 'Failed to transfer order');
      } else {
        toast.error('Failed to transfer order');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setSelectedWarehouseId('');
    onHide();
  };

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>Transfer Order</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {isLoading ? (
          <div className="text-center py-4">
            <Spinner animation="border" size="sm" />
            <span className="ms-2">Loading warehouses...</span>
          </div>
        ) : warehouses.length === 0 ? (
          <p className="text-muted text-center">
            No available warehouses to transfer
          </p>
        ) : (
          <Form.Group>
            <Form.Label>Select Target Warehouse</Form.Label>
            <Form.Select
              value={selectedWarehouseId}
              onChange={e => setSelectedWarehouseId(e.target.value)}
            >
              <option value="">-- Select Warehouse --</option>
              {warehouses.map(wh => (
                <option key={wh.id} value={wh.id}>
                  {wh.name || wh.domain}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit}
          disabled={isSubmitting || !selectedWarehouseId || isLoading}
        >
          {isSubmitting ? 'Transferring...' : 'Transfer'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default OrderTransferModal;
